import clsx from 'clsx';
import { format, isAfter, parse, parseISO, subDays } from 'date-fns';
import { groupBy, sumBy } from 'lodash-es';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui/Stack';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { ContentId } from 'src/utils/constants/contentId';
import { getCurrentLanguage } from 'src/utils/localeUtils';
import { AggregateMetricsTimePeriod } from 'src/WebApiController';

import {
  SaleChartData,
  useDataInsights,
} from '../DataInsightsChart/DataInsightsContext';
import { useGetAggregatedSaleMetrics } from '../DataInsightsChart/hooks/useGetAggregatedSaleMetrics';
import { useGetRecentSalesForEvents } from '../DataInsightsChart/hooks/useGetRecentSalesForEvents';
import { stitchNonCumulativeChartData } from '../DataInsightsChart/utils/stitchNonCumulativeChartData';
import * as styles from './InsightsSection.css';
export type InsightsData = {
  dateTime: string;
  totalRevenue: number;
  totalPnL: number;
  totalQuantity: number;
  marketplaceQuantities: Record<string, number>;
};

const getPast30DayAverages = (data: SaleChartData[]) => {
  const now = new Date();
  const thirtyDaysAgo = subDays(now, 30);

  const recentData = data.filter((d) =>
    isAfter(parseISO(d.dateTime), thirtyDaysAgo)
  );
  if (recentData.length === 0) return null;

  const byDay = groupBy(recentData, (d) =>
    parseISO(d.dateTime).toISOString().slice(0, 10)
  );
  const totalDays = Object.keys(byDay).length;

  const totalRevenue = sumBy(recentData, (d) => d.totalRevenue || 0);
  const totalPnl = sumBy(recentData, (d) => d.totalPnl || 0);
  const totalQuantity = sumBy(recentData, (d) => d.totalQuantity || 0);

  return {
    averageRevenue: totalRevenue / totalDays,
    averagePnL: totalPnl / totalDays,
    averageQuantity: totalQuantity / totalDays,
  };
};

const InsightsCell = ({
  metricContentId,
  currValue,
  averageValue,
  showBorder,
}: {
  metricContentId: string;
  currValue: string;
  averageValue: string;
  showBorder?: boolean;
}) => {
  const isMobile = useMatchMedia('mobile');
  return (
    <div
      className={clsx(styles.insightsCell, showBorder && styles.withTopBorder)}
    >
      <Stack className={styles.insightsCellPrimaryRow}>
        <div style={{ fontSize: vars.typography.fontSize.lg }}>
          <Content id={metricContentId} />
        </div>
        <div
          style={{
            fontSize: vars.typography.fontSize['2xl'],
            fontWeight: 700,
          }}
        >
          {currValue}
        </div>
      </Stack>
      <Stack className={styles.insightsCellSecondaryRow}>
        <div
          style={{
            fontSize: vars.typography.fontSize.base,
            color: vars.color.textPrimary,
          }}
        >
          {!isMobile ? (
            <Content id={ContentId.Last30DaysAverage} />
          ) : (
            <>Avg&nbsp;</>
          )}
        </div>
        <div style={{ fontSize: vars.typography.fontSize.base }}>
          {averageValue}
        </div>
      </Stack>
    </div>
  );
};

export const InsightsSection = () => {
  const {
    data: lastMonthData,
    isLoading,
    isFetching,
  } = useGetAggregatedSaleMetrics(AggregateMetricsTimePeriod.LastMonth);
  const { pastRecentSales } = useGetRecentSalesForEvents();

  const { timeZone } = useSiteTimezoneContext();

  const stitchedData = stitchNonCumulativeChartData(
    lastMonthData,
    pastRecentSales,
    timeZone,
    AggregateMetricsTimePeriod.LastMonth,
    false
  );

  const { uiCurrency } = useDataInsights();
  if (!stitchedData || stitchedData.length === 0 || isLoading) {
    return null;
  }
  const latestDayData = stitchedData[stitchedData.length - 1];
  const { totalRevenue, totalPnl, totalQuantity } = latestDayData;

  const averageData = getPast30DayAverages(stitchedData);
  const { averageRevenue, averagePnL, averageQuantity } = averageData || {
    averageRevenue: 0,
    averagePnL: 0,
    averageQuantity: 0,
  };

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: uiCurrency.code,
  });

  const quantityNumberFormat = new Intl.NumberFormat(getCurrentLanguage(), {
    useGrouping: true,
  });

  const todayDate = parse(
    stitchedData[stitchedData.length - 1].dateTime,
    'yyyy-MM-dd',
    new Date()
  );

  const formattedToday = format(todayDate, 'EEEE dd MMMM, yyyy');

  return (
    <Stack direction="column" style={{ marginTop: vars.spacing.xxl }}>
      <div className={styles.insightsSectionTitle}>{formattedToday}</div>
      <Stack className={styles.insightsTable}>
        <InsightsCell
          metricContentId={ContentId.Revenue}
          currValue={currencyFormatter.format(totalRevenue || 0)}
          averageValue={currencyFormatter.format(averageRevenue)}
        />
        <InsightsCell
          metricContentId={ContentId.ProfitNLoss}
          currValue={currencyFormatter.format(totalPnl || 0)}
          averageValue={`${averagePnL > 0 ? '+' : ''}${currencyFormatter.format(averagePnL)}`}
          showBorder
        />
        <InsightsCell
          metricContentId={ContentId.SoldTickets}
          currValue={quantityNumberFormat.format(totalQuantity || 0)}
          averageValue={quantityNumberFormat.format(
            Math.round(averageQuantity)
          )}
          showBorder
        />
      </Stack>
    </Stack>
  );
};
